import React, { useState, useEffect, useMemo } from 'react';
import { Card, Typography, Button, Tag, Skeleton, Row, Col, Divider, Radio, Table, message } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import { LocationExpense, ExpenseItem } from '../../../services/ExpenseService';
import { groupItemsByCriticalItem, getTierForLocation, CRITICAL_ITEMS_MAPPING, CriticalItem } from '../utils';
import { formatCurrency } from '../../../utils/formatters';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Text, Title } = Typography;

interface LocationComparisonViewProps {
  locationA: LocationExpense;
  locationB: LocationExpense;
  viewMode: 'weekly' | 'quarterly';
  onExit: () => void;
  item8WeekAveragesA?: Record<string, { averageQuantity: number; averageExpense: number }>;
  item8WeekAveragesB?: Record<string, { averageQuantity: number; averageExpense: number }>;
  item4WeekAveragesA?: Record<string, { averageQuantity: number; averageExpense: number }>;
  item4WeekAveragesB?: Record<string, { averageQuantity: number; averageExpense: number }>;
  loadingAverages?: boolean;
  onLoadAverages?: (location: string) => void;
  isMobile?: boolean;
}

const TIER_LABELS: Record<string, string> = {
  HIGH_VOLUME: 'High Volume',
  UPPER_MID: 'Upper Mid',
  LOWER_MID: 'Lower Mid',
  BASE: 'Base',
};

const TIER_COLORS: Record<string, string> = {
  HIGH_VOLUME: 'gold',
  UPPER_MID: 'blue',
  LOWER_MID: 'cyan',
  BASE: 'default',
};

// Extended types for aligned view
interface AlignedCriticalItem extends CriticalItem {
  isEmpty?: boolean;
}

interface AlignedExpenseItem extends ExpenseItem {
  isEmpty?: boolean;
}

interface LocationCardProps {
  location: LocationExpense;
  viewMode: 'weekly' | 'quarterly';
  item8WeekAverages?: Record<string, { averageQuantity: number; averageExpense: number }>;
  item4WeekAverages?: Record<string, { averageQuantity: number; averageExpense: number }>;
  loadingAverages?: boolean;
  onLoadAverages?: () => void;
  isMobile?: boolean;
  itemViewMode: 'critical' | 'all';
  onItemViewModeChange: (mode: 'critical' | 'all') => void;
  // Aligned mode props
  alignedItems?: boolean;
  alignedCriticalItems?: AlignedCriticalItem[];
  alignedAllItems?: AlignedExpenseItem[];
}

const LocationCard: React.FC<LocationCardProps> = ({
  location,
  viewMode,
  item8WeekAverages,
  item4WeekAverages,
  loadingAverages,
  onLoadAverages,
  isMobile,
  itemViewMode,
  onItemViewModeChange,
  alignedItems,
  alignedCriticalItems,
  alignedAllItems,
}) => {
  const tier = getTierForLocation(location.totalSales ?? 0, viewMode === 'quarterly');
  const criticalItems = useMemo(() => groupItemsByCriticalItem(location.items), [location.items]);
  const expensePercent = location.expensePercentageOfSales;

  // Load averages on mount for weekly view
  useEffect(() => {
    if (viewMode === 'weekly' && onLoadAverages) {
      onLoadAverages();
    }
  }, [viewMode, onLoadAverages]);

  const getGfsColor = (percent: number | undefined) => {
    if (percent === undefined) return undefined;
    if (percent > 15) return '#ff4d4f';
    if (percent > 13) return '#faad14';
    return '#52c41a';
  };

  // Helper to calculate critical item averages
  const calculateCriticalItemAvg = (
    criticalItem: AlignedCriticalItem,
    averages: Record<string, { averageQuantity: number; averageExpense: number }> | undefined
  ): number | null => {
    if (!averages || criticalItem.isEmpty) return null;
    const matchingItems = location.items.filter(locItem => {
      const criticalItemName = CRITICAL_ITEMS_MAPPING[locItem.itemName] || 'Other';
      return criticalItemName === criticalItem.criticalItemName;
    });
    let total = 0;
    let found = false;
    matchingItems.forEach(locItem => {
      const avg = averages[locItem.itemName];
      if (avg) { total += avg.averageQuantity; found = true; }
    });
    return found ? total : null;
  };

  // Use aligned items if provided, otherwise use location's own items
  const displayCriticalItems = alignedItems && alignedCriticalItems ? alignedCriticalItems : criticalItems;
  const displayAllItems = alignedItems && alignedAllItems ? alignedAllItems : location.items;

  // Critical Items Table Columns
  const criticalItemColumns = [
    {
      title: 'Item',
      dataIndex: 'criticalItemName',
      key: 'criticalItemName',
      width: isMobile ? 100 : 180,
      ellipsis: true,
      render: (name: string, record: AlignedCriticalItem) => (
        <span style={{ color: record.isEmpty ? '#999' : undefined }}>
          {name}
        </span>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      width: isMobile ? 60 : 80,
      align: 'right' as const,
      render: (qty: number, record: AlignedCriticalItem) =>
        record.isEmpty ? <span style={{ color: '#999' }}>--</span> : Math.round(qty).toLocaleString(),
      sorter: alignedItems ? undefined : (a: CriticalItem, b: CriticalItem) => a.totalQuantity - b.totalQuantity,
    },
    {
      title: isMobile ? 'Amt' : 'Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: isMobile ? 70 : 100,
      align: 'right' as const,
      render: (amount: number, record: AlignedCriticalItem) =>
        record.isEmpty ? <span style={{ color: '#999' }}>--</span> : formatCurrency(amount),
      sorter: alignedItems ? undefined : (a: CriticalItem, b: CriticalItem) => a.totalAmount - b.totalAmount,
      defaultSortOrder: alignedItems ? undefined : 'descend' as const,
    },
    ...(viewMode === 'weekly' ? [
      {
        title: isMobile ? '4-Wk' : '4-Wk Avg',
        key: '4weekAvgQty',
        width: isMobile ? 55 : 90,
        align: 'right' as const,
        render: (_: any, criticalItem: AlignedCriticalItem) => {
          if (criticalItem.isEmpty) return <span style={{ color: '#999' }}>--</span>;
          if (loadingAverages) {
            return <Skeleton.Input active size="small" style={{ width: 40 }} />;
          }
          const avgQty = calculateCriticalItemAvg(criticalItem, item4WeekAverages);
          return avgQty !== null ? Math.round(avgQty).toLocaleString() : '--';
        },
        sorter: alignedItems ? undefined : (a: CriticalItem, b: CriticalItem) => {
          const avgA = calculateCriticalItemAvg(a, item4WeekAverages) ?? 0;
          const avgB = calculateCriticalItemAvg(b, item4WeekAverages) ?? 0;
          return avgA - avgB;
        },
      },
      {
        title: isMobile ? '8-Wk' : '8-Wk Avg',
        key: '8weekAvgQty',
        width: isMobile ? 55 : 90,
        align: 'right' as const,
        render: (_: any, criticalItem: AlignedCriticalItem) => {
          if (criticalItem.isEmpty) return <span style={{ color: '#999' }}>--</span>;
          if (loadingAverages) {
            return <Skeleton.Input active size="small" style={{ width: 40 }} />;
          }
          const avgQty = calculateCriticalItemAvg(criticalItem, item8WeekAverages);
          return avgQty !== null ? Math.round(avgQty).toLocaleString() : '--';
        },
        sorter: alignedItems ? undefined : (a: CriticalItem, b: CriticalItem) => {
          const avgA = calculateCriticalItemAvg(a, item8WeekAverages) ?? 0;
          const avgB = calculateCriticalItemAvg(b, item8WeekAverages) ?? 0;
          return avgA - avgB;
        },
      },
    ] : []),
  ];

  // All Items Table Columns
  const allItemColumns = [
    {
      title: 'Item',
      dataIndex: 'itemName',
      key: 'itemName',
      width: isMobile ? 120 : 220,
      ellipsis: true,
      render: (name: string, record: AlignedExpenseItem) => (
        <span style={{ color: record.isEmpty ? '#999' : undefined }}>
          {name}
        </span>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'billedQuantity',
      key: 'billedQuantity',
      width: isMobile ? 60 : 80,
      align: 'right' as const,
      render: (qty: number, record: AlignedExpenseItem) =>
        record.isEmpty ? <span style={{ color: '#999' }}>--</span> : Math.round(qty).toLocaleString(),
      sorter: alignedItems ? undefined : (a: ExpenseItem, b: ExpenseItem) => a.billedQuantity - b.billedQuantity,
    },
    {
      title: isMobile ? 'Amt' : 'Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: isMobile ? 70 : 100,
      align: 'right' as const,
      render: (amount: number, record: AlignedExpenseItem) =>
        record.isEmpty ? <span style={{ color: '#999' }}>--</span> : formatCurrency(amount),
      sorter: alignedItems ? undefined : (a: ExpenseItem, b: ExpenseItem) => a.totalAmount - b.totalAmount,
      defaultSortOrder: alignedItems ? undefined : 'descend' as const,
    },
    ...(viewMode === 'weekly' ? [
      {
        title: isMobile ? '4-Wk' : '4-Wk Avg',
        key: '4weekAvgQty',
        width: isMobile ? 55 : 90,
        align: 'right' as const,
        render: (_: any, item: AlignedExpenseItem) => {
          if (item.isEmpty) return <span style={{ color: '#999' }}>--</span>;
          if (loadingAverages) {
            return <Skeleton.Input active size="small" style={{ width: 40 }} />;
          }
          const avg = item4WeekAverages?.[item.itemName];
          return avg ? Math.round(avg.averageQuantity).toLocaleString() : '--';
        },
        sorter: alignedItems ? undefined : (a: ExpenseItem, b: ExpenseItem) => {
          const avgA = item4WeekAverages?.[a.itemName]?.averageQuantity ?? 0;
          const avgB = item4WeekAverages?.[b.itemName]?.averageQuantity ?? 0;
          return avgA - avgB;
        },
      },
      {
        title: isMobile ? '8-Wk' : '8-Wk Avg',
        key: '8weekAvgQty',
        width: isMobile ? 55 : 90,
        align: 'right' as const,
        render: (_: any, item: AlignedExpenseItem) => {
          if (item.isEmpty) return <span style={{ color: '#999' }}>--</span>;
          if (loadingAverages) {
            return <Skeleton.Input active size="small" style={{ width: 40 }} />;
          }
          const avg = item8WeekAverages?.[item.itemName];
          return avg ? Math.round(avg.averageQuantity).toLocaleString() : '--';
        },
        sorter: alignedItems ? undefined : (a: ExpenseItem, b: ExpenseItem) => {
          const avgA = item8WeekAverages?.[a.itemName]?.averageQuantity ?? 0;
          const avgB = item8WeekAverages?.[b.itemName]?.averageQuantity ?? 0;
          return avgA - avgB;
        },
      },
    ] : []),
  ];

  return (
    <Card
      style={{ height: '100%' }}
      styles={{ body: { padding: isMobile ? 12 : 20 } }}
    >
      {/* Header */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <Title level={5} style={{ margin: 0, fontSize: isMobile ? 14 : 16 }}>
            {location.location}
          </Title>
          <Tag color={TIER_COLORS[tier]}>{TIER_LABELS[tier]}</Tag>
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>DM: {location.dm}</Text>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* Metrics Grid - Compact */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
        gap: isMobile ? '12px 16px' : '8px 12px',
        marginBottom: 12,
      }}>
        <div>
          <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
            {viewMode === 'weekly' ? 'Prior Wk Sales' : 'Qtr Sales'}
          </Text>
          <Text strong style={{ fontSize: 13 }}>
            {formatCurrency(location.totalSales ?? 0)}
          </Text>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
            {viewMode === 'weekly' ? 'Prior Wk GFS' : 'Qtr GFS'}
          </Text>
          <Text strong style={{ fontSize: 13 }}>
            {formatCurrency(location.totalExpense)}
          </Text>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
            GFS %
          </Text>
          <Text strong style={{ fontSize: isMobile ? 14 : 16, color: getGfsColor(expensePercent) }}>
            {expensePercent !== undefined && expensePercent > 0
              ? `${expensePercent.toFixed(1)}%`
              : '--'}
          </Text>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
            Items
          </Text>
          <Text strong style={{ fontSize: 13 }}>
            {Math.round(location.itemCount ?? location.items.length).toLocaleString()}
          </Text>
        </div>
      </div>

      {/* Toggle between Critical and All Items */}
      <div style={{ marginBottom: 8, textAlign: 'center' }}>
        <Radio.Group
          value={itemViewMode}
          onChange={(e) => onItemViewModeChange(e.target.value)}
          size="small"
          buttonStyle="solid"
        >
          <Radio.Button value="critical">Critical Items</Radio.Button>
          <Radio.Button value="all">All Items</Radio.Button>
        </Radio.Group>
      </div>

      {/* Data Table */}
      {itemViewMode === 'critical' ? (
        <Table
          key={`critical-${alignedItems ? 'aligned' : 'free'}`}
          columns={criticalItemColumns}
          dataSource={displayCriticalItems}
          rowKey="criticalItemName"
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          style={{ fontSize: 12 }}
          showSorterTooltip={!alignedItems}
          sortDirections={alignedItems ? [] : ['ascend', 'descend']}
        />
      ) : (
        <Table
          key={`all-${alignedItems ? 'aligned' : 'free'}`}
          columns={allItemColumns}
          dataSource={displayAllItems}
          rowKey={(item, index) => `${item.itemName}-${index}`}
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          style={{ fontSize: 12 }}
          showSorterTooltip={!alignedItems}
          sortDirections={alignedItems ? [] : ['ascend', 'descend']}
        />
      )}
    </Card>
  );
};

export const LocationComparisonView: React.FC<LocationComparisonViewProps> = ({
  locationA,
  locationB,
  viewMode,
  onExit,
  item8WeekAveragesA,
  item8WeekAveragesB,
  item4WeekAveragesA,
  item4WeekAveragesB,
  loadingAverages,
  onLoadAverages,
  isMobile,
}) => {
  const [itemViewMode, setItemViewMode] = useState<'critical' | 'all'>('critical');
  const [alignItems, setAlignItems] = useState(false);

  // Get critical items for both locations
  const criticalItemsA = useMemo(() => groupItemsByCriticalItem(locationA.items), [locationA.items]);
  const criticalItemsB = useMemo(() => groupItemsByCriticalItem(locationB.items), [locationB.items]);

  // Create aligned critical items (sorted alphabetically, with placeholders for missing items)
  const { alignedCriticalItemsA, alignedCriticalItemsB } = useMemo(() => {
    if (!alignItems) return { alignedCriticalItemsA: undefined, alignedCriticalItemsB: undefined };

    // Get all unique critical item names
    const allNames = new Set<string>();
    criticalItemsA.forEach(item => allNames.add(item.criticalItemName));
    criticalItemsB.forEach(item => allNames.add(item.criticalItemName));

    // Create maps for quick lookup
    const mapA = new Map(criticalItemsA.map(item => [item.criticalItemName, item]));
    const mapB = new Map(criticalItemsB.map(item => [item.criticalItemName, item]));

    // Sort by combined amount (A + B) descending — biggest spend first
    const sortedNames = Array.from(allNames).sort((a, b) => {
      const totalA = (mapA.get(a)?.totalAmount ?? 0) + (mapB.get(a)?.totalAmount ?? 0);
      const totalB = (mapA.get(b)?.totalAmount ?? 0) + (mapB.get(b)?.totalAmount ?? 0);
      return totalB - totalA;
    });

    // Build aligned arrays
    const alignedA: AlignedCriticalItem[] = sortedNames.map(name => {
      const item = mapA.get(name);
      if (item) return { ...item, isEmpty: false };
      return {
        criticalItemName: name,
        totalQuantity: 0,
        totalAmount: 0,
        items: [],
        isEmpty: true,
      };
    });

    const alignedB: AlignedCriticalItem[] = sortedNames.map(name => {
      const item = mapB.get(name);
      if (item) return { ...item, isEmpty: false };
      return {
        criticalItemName: name,
        totalQuantity: 0,
        totalAmount: 0,
        items: [],
        isEmpty: true,
      };
    });

    return { alignedCriticalItemsA: alignedA, alignedCriticalItemsB: alignedB };
  }, [alignItems, criticalItemsA, criticalItemsB]);

  // Create aligned all items (sorted alphabetically, with placeholders for missing items)
  const { alignedAllItemsA, alignedAllItemsB } = useMemo(() => {
    if (!alignItems) return { alignedAllItemsA: undefined, alignedAllItemsB: undefined };

    // Get all unique item names
    const allNames = new Set<string>();
    locationA.items.forEach(item => allNames.add(item.itemName));
    locationB.items.forEach(item => allNames.add(item.itemName));

    // Create maps for quick lookup
    const mapA = new Map(locationA.items.map(item => [item.itemName, item]));
    const mapB = new Map(locationB.items.map(item => [item.itemName, item]));

    // Sort by combined amount (A + B) descending — biggest spend first
    const sortedNames = Array.from(allNames).sort((a, b) => {
      const totalA = (mapA.get(a)?.totalAmount ?? 0) + (mapB.get(a)?.totalAmount ?? 0);
      const totalB = (mapA.get(b)?.totalAmount ?? 0) + (mapB.get(b)?.totalAmount ?? 0);
      return totalB - totalA;
    });

    // Build aligned arrays
    const alignedA: AlignedExpenseItem[] = sortedNames.map(name => {
      const item = mapA.get(name);
      if (item) return { ...item, isEmpty: false };
      return {
        itemName: name,
        billedQuantity: 0,
        totalAmount: 0,
        isEmpty: true,
      } as AlignedExpenseItem;
    });

    const alignedB: AlignedExpenseItem[] = sortedNames.map(name => {
      const item = mapB.get(name);
      if (item) return { ...item, isEmpty: false };
      return {
        itemName: name,
        billedQuantity: 0,
        totalAmount: 0,
        isEmpty: true,
      } as AlignedExpenseItem;
    });

    return { alignedAllItemsA: alignedA, alignedAllItemsB: alignedB };
  }, [alignItems, locationA.items, locationB.items]);

  // PDF Generation Function
  const generatePDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const today = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      // Title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Location Comparison Report', pageWidth / 2, 20, { align: 'center' });

      // Subtitle with date
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${today}`, pageWidth / 2, 28, { align: 'center' });
      doc.text(`View Mode: ${viewMode === 'weekly' ? 'Weekly' : 'Quarterly'}`, pageWidth / 2, 34, { align: 'center' });

      // Summary table for both locations
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Summary', 14, 46);

      const tierA = getTierForLocation(locationA.totalSales ?? 0, viewMode === 'quarterly');
      const tierB = getTierForLocation(locationB.totalSales ?? 0, viewMode === 'quarterly');

      autoTable(doc, {
        startY: 50,
        head: [['Metric', locationA.location, locationB.location, 'Difference']],
        body: [
          [
            'Tier',
            TIER_LABELS[tierA],
            TIER_LABELS[tierB],
            '-',
          ],
          [
            'DM',
            locationA.dm || '-',
            locationB.dm || '-',
            '-',
          ],
          [
            viewMode === 'weekly' ? 'Prior Wk Sales' : 'Qtr Sales',
            formatCurrency(locationA.totalSales ?? 0),
            formatCurrency(locationB.totalSales ?? 0),
            formatCurrency((locationA.totalSales ?? 0) - (locationB.totalSales ?? 0)),
          ],
          [
            viewMode === 'weekly' ? 'Prior Wk GFS' : 'Qtr GFS',
            formatCurrency(locationA.totalExpense),
            formatCurrency(locationB.totalExpense),
            formatCurrency(locationA.totalExpense - locationB.totalExpense),
          ],
          [
            'GFS %',
            locationA.expensePercentageOfSales
              ? `${locationA.expensePercentageOfSales.toFixed(1)}%`
              : '--',
            locationB.expensePercentageOfSales
              ? `${locationB.expensePercentageOfSales.toFixed(1)}%`
              : '--',
            locationA.expensePercentageOfSales && locationB.expensePercentageOfSales
              ? `${(locationA.expensePercentageOfSales - locationB.expensePercentageOfSales).toFixed(1)}%`
              : '--',
          ],
          [
            'Item Count',
            String(locationA.items.length),
            String(locationB.items.length),
            String(locationA.items.length - locationB.items.length),
          ],
        ],
        theme: 'striped',
        headStyles: { fillColor: [122, 31, 42] }, // Pita Way brand color
        styles: { fontSize: 9 },
      });

      // Get the Y position after the summary table
      const summaryEndY = (doc as any).lastAutoTable.finalY + 10;

      // Items comparison - Critical Items
      if (itemViewMode === 'critical') {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Critical Items Comparison', 14, summaryEndY);

        // Use aligned items if enabled, otherwise regular
        const itemsA = alignItems && alignedCriticalItemsA ? alignedCriticalItemsA : criticalItemsA;
        const itemsB = alignItems && alignedCriticalItemsB ? alignedCriticalItemsB : criticalItemsB;

        // Build comparison rows
        const allItemNames = new Set<string>();
        itemsA.forEach(item => allItemNames.add(item.criticalItemName));
        itemsB.forEach(item => allItemNames.add(item.criticalItemName));
        const sortedItemNames = Array.from(allItemNames).sort((a, b) => a.localeCompare(b));

        const mapA = new Map(itemsA.map(item => [item.criticalItemName, item as AlignedCriticalItem]));
        const mapB = new Map(itemsB.map(item => [item.criticalItemName, item as AlignedCriticalItem]));

        const comparisonRows = sortedItemNames.map(name => {
          const itemA = mapA.get(name);
          const itemB = mapB.get(name);
          const isEmptyA = itemA ? (itemA.isEmpty ?? false) : true;
          const isEmptyB = itemB ? (itemB.isEmpty ?? false) : true;
          const qtyA = itemA && !isEmptyA ? Math.round(itemA.totalQuantity) : 0;
          const qtyB = itemB && !isEmptyB ? Math.round(itemB.totalQuantity) : 0;
          const amtA = itemA && !isEmptyA ? itemA.totalAmount : 0;
          const amtB = itemB && !isEmptyB ? itemB.totalAmount : 0;

          return [
            name,
            itemA && !isEmptyA ? String(qtyA) : '--',
            itemA && !isEmptyA ? formatCurrency(amtA) : '--',
            itemB && !isEmptyB ? String(qtyB) : '--',
            itemB && !isEmptyB ? formatCurrency(amtB) : '--',
            qtyA !== 0 || qtyB !== 0 ? String(qtyA - qtyB) : '--',
          ];
        });

        autoTable(doc, {
          startY: summaryEndY + 4,
          head: [[
            'Item',
            `${locationA.location} Qty`,
            `${locationA.location} Amt`,
            `${locationB.location} Qty`,
            `${locationB.location} Amt`,
            'Qty Diff',
          ]],
          body: comparisonRows,
          theme: 'striped',
          headStyles: { fillColor: [122, 31, 42], fontSize: 8 },
          styles: { fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 45 },
            1: { halign: 'right' },
            2: { halign: 'right' },
            3: { halign: 'right' },
            4: { halign: 'right' },
            5: { halign: 'right' },
          },
        });
      } else {
        // All Items view
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('All Items Comparison', 14, summaryEndY);

        const itemsA = alignItems && alignedAllItemsA ? alignedAllItemsA : locationA.items;
        const itemsB = alignItems && alignedAllItemsB ? alignedAllItemsB : locationB.items;

        // Build comparison rows
        const allItemNames = new Set<string>();
        itemsA.forEach(item => allItemNames.add(item.itemName));
        itemsB.forEach(item => allItemNames.add(item.itemName));
        const sortedItemNames = Array.from(allItemNames).sort((a, b) => a.localeCompare(b));

        const mapA = new Map(itemsA.map(item => [item.itemName, item]));
        const mapB = new Map(itemsB.map(item => [item.itemName, item]));

        const comparisonRows = sortedItemNames.map(name => {
          const itemA = mapA.get(name);
          const itemB = mapB.get(name);
          const qtyA = itemA && !(itemA as AlignedExpenseItem).isEmpty ? Math.round(itemA.billedQuantity) : 0;
          const qtyB = itemB && !(itemB as AlignedExpenseItem).isEmpty ? Math.round(itemB.billedQuantity) : 0;
          const amtA = itemA && !(itemA as AlignedExpenseItem).isEmpty ? itemA.totalAmount : 0;
          const amtB = itemB && !(itemB as AlignedExpenseItem).isEmpty ? itemB.totalAmount : 0;

          return [
            name.length > 30 ? name.substring(0, 27) + '...' : name,
            itemA && !(itemA as AlignedExpenseItem).isEmpty ? String(qtyA) : '--',
            itemA && !(itemA as AlignedExpenseItem).isEmpty ? formatCurrency(amtA) : '--',
            itemB && !(itemB as AlignedExpenseItem).isEmpty ? String(qtyB) : '--',
            itemB && !(itemB as AlignedExpenseItem).isEmpty ? formatCurrency(amtB) : '--',
            qtyA !== 0 || qtyB !== 0 ? String(qtyA - qtyB) : '--',
          ];
        });

        autoTable(doc, {
          startY: summaryEndY + 4,
          head: [[
            'Item',
            `${locationA.location.substring(0, 10)} Qty`,
            `${locationA.location.substring(0, 10)} Amt`,
            `${locationB.location.substring(0, 10)} Qty`,
            `${locationB.location.substring(0, 10)} Amt`,
            'Qty Diff',
          ]],
          body: comparisonRows,
          theme: 'striped',
          headStyles: { fillColor: [122, 31, 42], fontSize: 7 },
          styles: { fontSize: 7 },
          columnStyles: {
            0: { cellWidth: 40 },
            1: { halign: 'right' },
            2: { halign: 'right' },
            3: { halign: 'right' },
            4: { halign: 'right' },
            5: { halign: 'right' },
          },
        });
      }

      // Footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Pita Way GFS Tracking System - Page ${i} of ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      // Save the PDF
      const fileName = `comparison_${locationA.location.replace(/\s+/g, '-')}_vs_${locationB.location.replace(/\s+/g, '-')}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      message.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to generate PDF');
    }
  };

  return (
    <div>
      <div style={{
        marginBottom: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onExit}
        >
          Exit Comparison
        </Button>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type={alignItems ? 'primary' : 'default'}
            onClick={() => setAlignItems(!alignItems)}
            style={{
              fontWeight: alignItems ? 600 : 400,
            }}
          >
            {alignItems ? '✓ Items Aligned' : 'Align Items'}
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={generatePDF}
          >
            Download PDF
          </Button>
        </div>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <LocationCard
            location={locationA}
            viewMode={viewMode}
            item8WeekAverages={item8WeekAveragesA}
            item4WeekAverages={item4WeekAveragesA}
            loadingAverages={loadingAverages}
            onLoadAverages={() => onLoadAverages?.(locationA.location)}
            isMobile={isMobile}
            itemViewMode={itemViewMode}
            onItemViewModeChange={setItemViewMode}
            alignedItems={alignItems}
            alignedCriticalItems={alignedCriticalItemsA}
            alignedAllItems={alignedAllItemsA}
          />
        </Col>
        <Col xs={24} md={12}>
          <LocationCard
            location={locationB}
            viewMode={viewMode}
            item8WeekAverages={item8WeekAveragesB}
            item4WeekAverages={item4WeekAveragesB}
            loadingAverages={loadingAverages}
            onLoadAverages={() => onLoadAverages?.(locationB.location)}
            isMobile={isMobile}
            itemViewMode={itemViewMode}
            onItemViewModeChange={setItemViewMode}
            alignedItems={alignItems}
            alignedCriticalItems={alignedCriticalItemsB}
            alignedAllItems={alignedAllItemsB}
          />
        </Col>
      </Row>
    </div>
  );
};
